<script setup lang="ts">
import type { ElementInterface, GroupInterface, QuerySliceInterface, SliceConfigInterface, SliceInterface } from '@voix/types'

import { useEvent } from '@voix/composables/useEventBus'
import { useSliceStore } from '@voix/store/sliceStore'
import { computed } from 'vue'

import VoixQuerySlice from './VoixQuerySlice.vue'

const props = defineProps<{
  element: ElementInterface | SliceInterface | GroupInterface | QuerySliceInterface
}>()

const sliceStore = useSliceStore()
const voixSliceConfigurations = computed(() => {
  const sliceConfiguration = sliceStore.slices[props.element.component as string] as SliceConfigInterface
  if (!sliceConfiguration) {
    console.error(`Slice ${props.element.component} not found`)
    return null
  }
  return sliceConfiguration
})

function getSlotElements(slot: string) {
  return props.element.elements?.filter((el) => {
    if (!el.slot && slot === 'default')
      return true

    return el.slot === slot
  }) as ElementInterface[]
}
function shouldRenderElement(element: ElementInterface) {
  const sliceStore = useSliceStore()
  const config = sliceStore.getSlice(element.component)

  if (config?.xp_management === 'manual')
    return true

  return (
    (element.enabled && element.xp_status === 'approved')
    || (typeof element.xp_status === 'undefined' || typeof element.enabled === 'undefined') // legacy
  )
}

function logError(error: any) {
  let msg = error

  if (Object.prototype.hasOwnProperty.call(error, 'message'))
    msg = error.message

  if (typeof window === 'object'
    && msg.includes('Failed to fetch dynamically imported module')
  ) {
    // very likely that we released a new version so let's try to refresh
    let url = window.location.href
    const queryString = new URLSearchParams(window.location.search)

    if (!queryString.has('v-fresh')) {
      // only if fresh is not on the page
      if (url.includes('?'))
        url += '&v-fresh=1'
      else
        url += '?v-fresh=1'

      window.location.href = url
    }
  }

  useEvent('voix:error', error)

  console.error(error)

  return error
}
// Hydration strategies
const hydrateOnIdle = computed(() => {
  return props.element.hydration_mode === 'idle' ? 10000 : undefined
})
const hydrateOnVisible = computed(() => {
  return props.element.hydration_mode === 'visible' ? true : undefined
})
const hydrateOnInteraction = computed(() => {
  return props.element.hydration_mode === 'mouseover' ? 'mouseover' : undefined
})
const hydrateNever = computed(() => {
  return props.element.hydration_mode === 'never' ? true : undefined
})
</script>

<template>
  <template v-if="props.element && props.element.type === 'slice' && shouldRenderElement(element)">
    <NuxtErrorBoundary @error="logError">
      <template v-if="voixSliceConfigurations">
        <LazyIdleVoixSlice
          v-if="props.element.hydration_mode === 'idle'"
          :slice="element as SliceInterface"
          hydrate-on-idle
        >
          <template
            v-for="{ name } in voixSliceConfigurations.slots"
            :key="name"
            #[name]
          >
            <LazyVoixElementsLoop :elements="getSlotElements(name)" />
          </template>
        </LazyIdleVoixSlice>
        <LazyInteractionVoixSlice
          v-else-if="props.element.hydration_mode === 'mouseover'"
          :slice="element as SliceInterface"
          hydrate-on-interaction="mouseover"
        >
          <template
            v-for="{ name } in voixSliceConfigurations.slots"
            :key="name"
            #[name]
          >
            <LazyVoixElementsLoop :elements="getSlotElements(name)" />
          </template>
        </LazyInteractionVoixSlice>
        <LazyVisibleVoixSlice
          v-else-if="props.element.hydration_mode === 'visible'"
          :slice="element as SliceInterface"
          hydrate-on-visible
        >
          <template
            v-for="{ name } in voixSliceConfigurations.slots"
            :key="name"
            #[name]
          >
            <LazyVoixElementsLoop :elements="getSlotElements(name)" />
          </template>
        </LazyVisibleVoixSlice>
        <LazyNeverVoixSlice
          v-else-if="props.element.hydration_mode === 'never'"
          :slice="element as SliceInterface"
          hydrate-never
        >
          <template
            v-for="{ name } in voixSliceConfigurations.slots"
            :key="name"
            #[name]
          >
            <LazyVoixElementsLoop :elements="getSlotElements(name)" />
          </template>
        </LazyNeverVoixSlice>
        <ClientOnly v-else-if="props.element.hydration_mode === 'client-only'">
          <LazyVoixSlice
            :slice="element as SliceInterface"
          >
            <template
              v-for="{ name } in voixSliceConfigurations.slots"
              :key="name"
              #[name]
            >
              <LazyVoixElementsLoop :elements="getSlotElements(name)" />
            </template>
          </LazyVoixSlice>
        </ClientOnly>
        <LazyVoixSlice
          v-else
          :slice="element as SliceInterface"
        >
          <template
            v-for="{ name } in voixSliceConfigurations.slots"
            :key="name"
            #[name]
          >
            <LazyVoixElementsLoop :elements="getSlotElements(name)" />
          </template>
        </LazyVoixSlice>
      </template>
    </NuxtErrorBoundary>
  </template>

  <VoixQuerySlice
    v-if="props.element.type === 'query' && shouldRenderElement(element)"
    :element="props.element as QuerySliceInterface"
  />
</template>
